import React, { useCallback, useContext, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { Notification } from '@workboard/wobo-ui';

export enum NotificationSeverity {
  Success = 'success',
  Error = 'error',
}

type NotificationText = string | React.ReactChild;

interface NotificationContext {
  notify(message: NotificationText, severity: NotificationSeverity): void;
  success(message: NotificationText): void;
  error(message: NotificationText): void;
}

export const NotificationContext = React.createContext<NotificationContext>({
  notify: () => null,
  success: () => null,
  error: () => null,
});

interface Notification {
  id: string;
  message: NotificationText;
  severity: NotificationSeverity;
}

export const ToastNotificationProvider = ({ children }: React.PropsWithChildren) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const notify = useCallback(
    (message: NotificationText, severity: NotificationSeverity) => {
      setNotifications((items) => [
        ...items,
        { id: uuid(), message, severity },
      ]);
    },
    []
  );
  const success = useCallback(
    (message: string) => notify(message, NotificationSeverity.Success),
    [notify]
  );
  const error = useCallback(
    (message: string) => notify(message, NotificationSeverity.Error),
    [notify]
  );

  const removeNotification = useCallback((notification: Notification) => {
    setNotifications((items) => items.filter((i) => i !== notification));
  }, []);

  return (
    <NotificationContext.Provider value={{ notify, success, error }}>
      {children}
      {notifications.map((notification) => (
        <Notification
          key={notification.id}
          open
          onClose={() => removeNotification(notification)}
          severity={notification.severity}
          message={notification.message}
          anchorOrigin={{
            horizontal: 'center',
            vertical: 'bottom',
          }}
        />
      ))}
    </NotificationContext.Provider>
  );
};

export type CallbackNotificationProviderProps = {
  onMessage: (
    message: NotificationText,
    severity: NotificationSeverity
  ) => void;
};
export const CallbackNotificationProvider = ({ children, onMessage }: React.PropsWithChildren<CallbackNotificationProviderProps>) => {
  const notify = useCallback(
    (message: NotificationText, severity: NotificationSeverity) => {
      onMessage(message, severity);
    },
    [onMessage]
  );
  const success = useCallback(
    (message: string) => notify(message, NotificationSeverity.Success),
    [notify]
  );
  const error = useCallback(
    (message: string) => notify(message, NotificationSeverity.Error),
    [notify]
  );

  return (
    <NotificationContext.Provider value={{ notify, success, error }}>
      {children}
    </NotificationContext.Provider>
  );
};

export function useNotification() {
  return useContext(NotificationContext);
}
